import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { CompanyType, Profile } from '../types/profile';
import type { Role } from '../types/auth';
import { getPermissionsForRole } from '../utils/permissions';
import { ServiceCategory } from '../types/service';
import { PostgrestError } from '@supabase/supabase-js';
import { Industry } from './industryStore';
import { useServiceCategoryStore } from './serviceCategoryStore';

export type AchievementFormData = {
  id?: string;
  profile_id: string;
  client_name: string;
  project_name: string;
  project_description: string;
  project_image_url?: string;
  service_category_ids: number[];
  imageFile?: File;
};

interface ProfileState {
  profile: Profile | null;
  role: Role | null;
  permissions: string[];
  loading: boolean;
  error: string | null;
  searchResults: Profile[];
  setProfile: (profile: Profile | null) => void;
  setRole: (role: Role | null) => void;
  setPermissions: (permissions: string[]) => void;
  setLoading: (loading: boolean) => void;
  fetchProfile: (userId: string, options?: { force?: boolean }) => Promise<Profile | null>;
  uploadAvatar: (profileId: string, file: File) => Promise<string>;
  completeProfile: (params: {
    userId: string;
    role?: Role;
    companyType?: CompanyType | null | undefined;
    firstName?: string;
    lastName?: string;
    languages?: string[];
    cityId?: number | null | undefined;
    bio?: string | null | undefined;
    serviceIds?: number[];
    jobTitle?: string | null | undefined;
    companyName?: string | null | undefined;
    organizationWebsite?: string | null | undefined;
    organizationNumber?: string | null | undefined;
    industries?: number[];
    phone?: string | null | undefined;
    postalCode?: string | null | undefined;
  }) => Promise<void>;
  updateProfileInfo: (params: {
    userId: string;
    firstName: string;
    lastName: string;
    postalCode: string | null | undefined;
    bio: string | null | undefined;
    cityId: number | null;
    languages: string[];
    phone?: string | null | undefined;
  }) => Promise<void>;
  upsertProfileType: (
    userId: string,
    firstName: string,
    role: Role,
    notificationAccepted?: boolean,
    newsletterAccepted?: boolean
  ) => Promise<void>;
  updateProfileServices: (userId: string, newServiceCategoryIds: number[]) => Promise<void>;
  upsertAchievement: (achievement: AchievementFormData) => Promise<void>;
  deleteAchievement: (id: string) => Promise<void>;
  suggestNewService: (service: {
    label: string;
    description?: string | null;
  }) => Promise<ServiceCategory>;
  approveSuggestedCategory: (
    categoryId: number,
    labels?: { labelFr?: string; labelEn?: string | null }
  ) => Promise<PostgrestError | null>;

  suggestNewIndustry: (industry: {
    label: string;
    description?: string | null;
  }) => Promise<Industry>;
  approveSuggestedIndustry: (categoryId: number) => Promise<PostgrestError | null>;
}

let activeProfileFetch: Promise<Profile | null> | null = null;
let activeProfileFetchUserId: string | null = null;

export class UploadAchievementImageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadAchievementImageError';
  }
}

const uploadAchievementImage = async (
  profileId: string,
  achievementId: string,
  imageFile: File
) => {
  const fileExt = imageFile.name.split('.').pop();
  const filePath = `${profileId}/${achievementId}.${fileExt}`;

  // Upload the image
  try {
    const { error: uploadError } = await supabase.storage
      .from('achievements-images')
      .upload(filePath, imageFile, {
        upsert: true,
      });

    if (uploadError) throw new UploadAchievementImageError(uploadError.message);

    // Get the public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from('achievements-images').getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    throw new UploadAchievementImageError((error as Error).message);
  }
};

export const useProfileStore = create<ProfileState>((set, get) => ({
  profile: null,
  role: null,
  permissions: [],
  loading: false,
  error: null,
  searchResults: [],

  setProfile: profile => set({ profile }),
  setRole: role => set({ role }),
  setPermissions: permissions => set({ permissions }),
  setLoading: loading => set({ loading }),

  fetchProfile: async (userId: string, options: { force?: boolean } = {}) => {
    if (get().loading && !options.force) {
      if (activeProfileFetch && activeProfileFetchUserId === userId) {
        return activeProfileFetch;
      }
      return get().profile;
    }

    set({ loading: true });
    activeProfileFetchUserId = userId;

    activeProfileFetch = (async () => {
      try {
        const { data: profileData, error } = await supabase
          .from('profiles')
          .select(
            `
          *,
          city:cities (id, name, latitude, longitude),
          services:services(
            is_active,
            service_category:service_category_id(
              id,
              slug,
              label
            )
          ),
          industries (
            id,
            slug,
            label
          ),
          achievements (
            id,
            client_name,
            project_name,
            project_description,
            project_image_url,
            created_at,
            achievement_services (
              service_category:service_category_id (
                id,
              slug,
              label
            )
          )
       )
        `
          )
          .eq('id', userId)
          .maybeSingle();

        if (error) throw error;

        if (!profileData) {
          set({
            profile: null,
            role: null,
            permissions: [],
            loading: false,
          });
          return null;
        }

        const profile = {
          ...profileData,
          services: profileData?.services
            ?.filter((service: { is_active: boolean }) => service.is_active)
            .map((service: { service_category: ServiceCategory }) => service.service_category),
          achievements:
            profileData?.achievements?.map(
              (achievement: {
                id?: string;
                profile_id: string;
                client_name: string;
                project_name: string;
                project_description: string;
                project_image_url?: string;
                created_at: string;
                achievement_services?: { service_category: ServiceCategory }[];
              }) => ({
                id: achievement.id,
                profile_id: achievement.profile_id,
                client_name: achievement.client_name,
                project_name: achievement.project_name,
                project_description: achievement.project_description,
                project_image_url: achievement.project_image_url,
                services:
                  achievement.achievement_services?.map(
                    (as: { service_category: ServiceCategory }) => as.service_category
                  ) || [],
              })
            ) || [],
        };

        set({
          profile,
          role: profile?.role || null,
          permissions: getPermissionsForRole(profile?.role),
          loading: false,
        });
        return profile as Profile;
      } catch (error) {
        set({ loading: false, error: (error as Error).message });
        console.error('Error fetching profile:', error);
        return null;
      } finally {
        activeProfileFetch = null;
        activeProfileFetchUserId = null;
      }
    })();

    return activeProfileFetch;
  },

  uploadAvatar: async (profileId: string, file: File) => {
    const fileExt = file.name.split('.').pop();
    const filePath = `${profileId}/avatar.${fileExt}`;

    const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file, {
      upsert: true,
    });

    if (uploadError) {
      throw uploadError;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from('avatars').getPublicUrl(filePath);

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: publicUrl })
      .eq('id', profileId);

    if (updateError) {
      throw updateError;
    }

    // Refresh profile data after update
    await get().fetchProfile(profileId);

    return publicUrl;
  },

  completeProfile: async ({
    userId,
    role,
    companyType,
    firstName,
    lastName,
    languages,
    cityId,
    bio,
    serviceIds,
    jobTitle,
    companyName,
    organizationWebsite,
    organizationNumber,
    industries,
    phone,
    postalCode,
  }) => {
    const { error } = await supabase.rpc('complete_profile', {
      p_user_id: userId,
      p_role: role || get().profile?.role,
      p_company_type: companyType || get().profile?.company_type,
      p_first_name: firstName?.trim() || get().profile?.first_name,
      p_last_name: lastName?.trim() || get().profile?.last_name,
      p_languages: languages || get().profile?.languages,
      p_city_id: cityId ?? get().profile?.city_id ?? null,
      p_bio: bio ?? get().profile?.bio,
      p_service_ids: serviceIds || get().profile?.services?.map(service => service.id) || [],
      p_job_title: jobTitle?.trim() || get().profile?.job_title,
      p_company_name: companyName?.trim() || get().profile?.company_name,
      p_organization_website:
        organizationWebsite === undefined
          ? get().profile?.organization_website
          : (organizationWebsite?.trim() ?? null),
      p_organization_number:
        organizationNumber === undefined
          ? get().profile?.organization_number
          : (organizationNumber?.trim() ?? null),
      p_industries: industries || get().profile?.industries?.map(industry => industry.id) || [],
      p_phone: phone?.trim() ?? get().profile?.phone,
      p_postal_code: postalCode?.trim() ?? get().profile?.postal_code,
    });

    if (error) {
      throw error;
    }
    // Refresh profile data after update
    await get().fetchProfile(userId, { force: true });
  },

  updateProfileInfo: async ({
    userId,
    firstName,
    lastName,
    postalCode,
    bio,
    cityId,
    languages,
    phone,
  }) => {
    const { error } = await supabase
      .from('profiles')
      .update({
        city_id: cityId,
        languages,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        postal_code: postalCode?.trim(),
        bio: bio?.trim(),

        phone: phone?.trim(),
      })
      .eq('id', userId);

    if (error) {
      throw error;
    }

    // Refresh profile data after update
    await get().fetchProfile(userId);
  },

  upsertProfileType: async (
    userId: string,
    firstName: string,
    role: Role,
    notificationAccepted?: boolean,
    newsletterAccepted?: boolean
  ) => {
    // Get user metadata if not provided
    let notificationAcceptedValue = notificationAccepted;
    let newsletterAcceptedValue = newsletterAccepted;

    if (notificationAcceptedValue === undefined || newsletterAcceptedValue === undefined) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user?.user_metadata) {
        if (notificationAcceptedValue === undefined) {
          notificationAcceptedValue = user.user_metadata.notificationAccepted ?? false;
        }
        if (newsletterAcceptedValue === undefined) {
          newsletterAcceptedValue = user.user_metadata.newsletterAccepted ?? false;
        }
      } else {
        if (notificationAcceptedValue === undefined) {
          notificationAcceptedValue = false;
        }
        if (newsletterAcceptedValue === undefined) {
          newsletterAcceptedValue = false;
        }
      }
    }

    const { error } = await supabase.rpc('upsert_profile_role', {
      p_id: userId,
      p_first_name: firstName,
      p_role: role,
      p_notification_accepted: notificationAcceptedValue,
      p_newsletter_accepted: newsletterAcceptedValue,
    });

    if (error) throw error;

    await get().fetchProfile(userId);
  },

  updateProfileServices: async (userId: string, newServiceCategoryIds: number[]) => {
    const { error } = await supabase.rpc('update_freelancer_services', {
      p_freelancer_id: userId,
      p_new_service_category_ids: newServiceCategoryIds,
    });
    if (error) throw error;

    // Refresh profile
    await get().fetchProfile(userId, { force: true });
  },

  upsertAchievement: async achievement => {
    let achievementImageUrl = null;

    if (achievement.id && achievement.imageFile) {
      achievementImageUrl = await uploadAchievementImage(
        achievement.profile_id,
        achievement.id,
        achievement.imageFile
      );
    }

    const { data: achievementId, error: rpcError } = await supabase.rpc(
      'upsert_profile_achievement',
      {
        p_achievement_id: achievement.id || null,
        p_profile_id: achievement.profile_id,
        p_client_name: achievement.client_name,
        p_project_name: achievement.project_name,
        p_project_description: achievement.project_description,
        p_project_image_url: achievementImageUrl,
        p_service_category_ids: achievement.service_category_ids,
      }
    );

    if (rpcError) throw rpcError;

    // Handle image upload if there's a new image
    if (!achievement.id && achievement.imageFile) {
      achievementImageUrl = await uploadAchievementImage(
        achievement.profile_id,
        achievementId,
        achievement.imageFile
      );

      // Update the achievement with the image URL
      const { error: updateError } = await supabase
        .from('achievements')
        .update({ project_image_url: achievementImageUrl })
        .eq('id', achievementId);

      if (updateError) throw updateError;
    }

    await get().fetchProfile(achievement.profile_id);
  },

  deleteAchievement: async id => {
    const { error } = await supabase.from('achievements').delete().eq('id', id);
    if (error) throw error;
    await get().fetchProfile(get().profile!.id);
  },

  suggestNewService: async (service: { label: string; description?: string | null }) => {
    const { error, data } = await supabase.rpc('suggest_service_category', {
      p_label: service.label,
      p_description: service.description,
    });

    if (error) throw error;

    const serviceCategory = data as ServiceCategory;
    useServiceCategoryStore.getState().upsertServiceCategory(serviceCategory);
    return serviceCategory;
  },

  approveSuggestedCategory: async (categoryId, labels) => {
    const { data, error } = await supabase.rpc('approve_suggested_category', {
      category_id: categoryId,
      p_label_fr: labels?.labelFr ?? null,
      p_label_en: labels?.labelEn ?? null,
    });

    if (error) throw error;

    return data;
  },

  suggestNewIndustry: async (industry: { label: string; description?: string | null }) => {
    const { error, data } = await supabase.rpc('suggest_industry', {
      p_label: industry.label,
      p_description: industry.description,
    });
    if (error) throw error;

    return data as Industry;
  },

  approveSuggestedIndustry: async (industryId: number) => {
    const { data, error } = await supabase.rpc('approve_suggested_industry', {
      industry_id: industryId,
    });
    if (error) throw error;

    return data;
  },
}));
